import { useState, useRef, useEffect, useCallback } from 'react';
import { Bot, Send, X, Sparkles, Loader2, Check } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from './ui/button';

const MAX_MESSAGES = 50;
const WELCOME_MESSAGE = "Ciao! Posso aiutarti a modificare task o creare task, progetti e risorse. Scrivi cosa vuoi fare.";
const MODEL = 'gemini-2.5-flash';

export const AiAgentBubble = ({ authAxios, apiBase, onActionExecuted }) => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);
  const [welcomed, setWelcomed] = useState(false);

  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  const pushMessages = useCallback(function(newMsgs) {
    setMessages(function(prev) {
      const combined = prev.concat(newMsgs);
      if (combined.length > MAX_MESSAGES) {
        return combined.slice(combined.length - MAX_MESSAGES);
      }
      return combined;
    });
  }, []);

  // Auto-scroll on new messages or when opening
  useEffect(function() {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, pendingAction, open]);

  // Welcome message on first open
  useEffect(function() {
    if (open && !welcomed) {
      setWelcomed(true);
      setMessages([{ role: 'assistant', text: WELCOME_MESSAGE }]);
      setTimeout(function() {
        if (inputRef.current) {
          inputRef.current.focus();
        }
      }, 50);
    }
  }, [open, welcomed]);

  const handleResponse = useCallback(function(data) {
    const reply = data && data.reply ? data.reply : '';
    const pending = data && data.pendingAction ? data.pendingAction : null;
    const executed = data && data.executedAction ? data.executedAction : null;

    const newMsgs = [];
    if (reply) {
      newMsgs.push({ role: 'assistant', text: reply });
    }
    if (executed) {
      const resultText = executed.result ? String(executed.result) : 'Azione eseguita.';
      newMsgs.push({ role: 'system', text: resultText, executed: true });
    }
    if (newMsgs.length > 0) {
      pushMessages(newMsgs);
    }

    setPendingAction(pending);

    if (executed) {
      toast.success('Azione eseguita');
      if (typeof onActionExecuted === 'function') {
        onActionExecuted(executed);
      }
    }
  }, [pushMessages, onActionExecuted]);

  const sendRequest = useCallback(async function(messagesPayload, confirmedAction) {
    setLoading(true);
    try {
      const response = await authAxios.post(apiBase + '/ai-agent/chat', {
        messages: messagesPayload,
        confirmedAction: confirmedAction || null,
        model: MODEL
      });
      handleResponse(response.data);
    } catch (err) {
      const detail = err && err.response && err.response.data && err.response.data.detail
        ? err.response.data.detail
        : 'Errore';
      toast.error(typeof detail === 'string' ? detail : 'Errore');
      pushMessages([{ role: 'system', text: 'Errore: ' + (typeof detail === 'string' ? detail : 'richiesta fallita') }]);
    } finally {
      setLoading(false);
      setTimeout(function() {
        if (inputRef.current) {
          inputRef.current.focus();
        }
      }, 50);
    }
  }, [authAxios, apiBase, handleResponse, pushMessages]);

  const buildPayload = useCallback(function(currentMessages) {
    return currentMessages
      .filter(function(m) { return m.role === 'user' || m.role === 'assistant'; })
      .map(function(m) { return { role: m.role, text: m.text }; });
  }, []);

  const handleSend = useCallback(function() {
    const trimmed = input.trim();
    if (!trimmed || loading || pendingAction) return;
    const userMsg = { role: 'user', text: trimmed };
    const nextMessages = messages.concat([userMsg]);
    setMessages(function(prev) {
      const combined = prev.concat([userMsg]);
      if (combined.length > MAX_MESSAGES) {
        return combined.slice(combined.length - MAX_MESSAGES);
      }
      return combined;
    });
    setInput('');
    sendRequest(buildPayload(nextMessages), null);
  }, [input, loading, pendingAction, messages, sendRequest, buildPayload]);

  const handleConfirm = useCallback(function() {
    if (!pendingAction || loading) return;
    const action = pendingAction;
    setPendingAction(null);
    sendRequest(buildPayload(messages), { tool: action.tool, args: action.args });
  }, [pendingAction, loading, messages, sendRequest, buildPayload]);

  const handleCancel = useCallback(function() {
    if (!pendingAction) return;
    setPendingAction(null);
    pushMessages([{ role: 'assistant', text: 'Ok, ho annullato.' }]);
  }, [pendingAction, pushMessages]);

  const handleKeyDown = function(e) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  if (!open) {
    return (
      <button
        type="button"
        onClick={function() { setOpen(true); }}
        aria-label="Apri assistente AI"
        className="fixed bottom-6 right-6 z-50 w-14 h-14 bg-primary text-primary-foreground rounded-full shadow-lg hover:scale-105 transition flex items-center justify-center"
        data-testid="ai-agent-bubble-open"
      >
        <Bot size={24} />
      </button>
    );
  }

  return (
    <div
      className="fixed bottom-6 right-6 z-50 w-[380px] h-[600px] max-h-[80vh] bg-card border border-border rounded-xl shadow-2xl flex flex-col"
      data-testid="ai-agent-bubble-window"
    >
      {/* Header */}
      <div className="h-14 border-b border-border flex items-center justify-between px-4 py-3 flex-shrink-0">
        <div className="flex items-center gap-2">
          <Sparkles size={18} className="text-primary" />
          <span className="font-medium text-sm">Assistente AI</span>
        </div>
        <button
          type="button"
          onClick={function() { setOpen(false); }}
          aria-label="Chiudi assistente AI"
          className="text-muted-foreground hover:text-foreground transition"
          data-testid="ai-agent-bubble-close"
        >
          <X size={18} />
        </button>
      </div>

      {/* Body */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3">
        {messages.map(function(msg, idx) {
          if (msg.role === 'user') {
            return (
              <div key={idx} className="bg-primary text-primary-foreground rounded-lg p-3 max-w-[80%] ml-auto text-sm whitespace-pre-wrap break-words">
                {msg.text}
              </div>
            );
          }
          if (msg.role === 'assistant') {
            return (
              <div key={idx} className="bg-muted rounded-lg p-3 max-w-[80%] text-sm whitespace-pre-wrap break-words">
                {msg.text}
              </div>
            );
          }
          // system
          return (
            <div key={idx} className="text-xs text-muted-foreground italic text-center flex items-center justify-center gap-1">
              {msg.executed ? <Check size={12} className="inline" /> : null}
              <span>{msg.text}</span>
            </div>
          );
        })}
        {loading && (
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <Loader2 size={14} className="animate-spin" />
            <span>Sto pensando…</span>
          </div>
        )}
      </div>

      {/* Pending action card */}
      {pendingAction && (
        <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-3 m-4 mb-0">
          <div className="flex items-center gap-2 mb-1">
            <Sparkles size={14} className="text-amber-600 dark:text-amber-400" />
            <span className="text-sm font-medium">Azione proposta</span>
          </div>
          <p className="text-sm mb-3">{pendingAction.humanSummary}</p>
          <div className="flex gap-2 justify-end">
            <Button
              size="sm"
              variant="outline"
              onClick={handleCancel}
              disabled={loading}
              data-testid="ai-agent-cancel-action"
            >
              Annulla
            </Button>
            <Button
              size="sm"
              onClick={handleConfirm}
              disabled={loading}
              data-testid="ai-agent-confirm-action"
            >
              {loading ? <Loader2 size={14} className="animate-spin mr-1" /> : null}
              Conferma
            </Button>
          </div>
        </div>
      )}

      {/* Footer input */}
      <div className="border-t border-border p-3 flex gap-2 flex-shrink-0">
        <input
          ref={inputRef}
          type="text"
          value={input}
          onChange={function(e) { setInput(e.target.value); }}
          onKeyDown={handleKeyDown}
          placeholder="Scrivi un comando…"
          disabled={loading || pendingAction !== null}
          className="flex-1 h-9 rounded-md border border-input px-3 text-sm bg-background focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-50"
          data-testid="ai-agent-input"
        />
        <Button
          size="icon"
          onClick={handleSend}
          disabled={loading || pendingAction !== null || !input.trim()}
          aria-label="Invia messaggio"
          className="h-9 w-9"
          data-testid="ai-agent-send"
        >
          {loading ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
        </Button>
      </div>
    </div>
  );
};
